import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSaveData } from '../../../hooks/useSaveData';
import { usersApiKeyStore } from '../../../api/users-api-key-store';
import { Alert, Box, Button, Checkbox, FormControl, Grid, InputLabel, LinearProgress, MenuItem, Select, Snackbar, Tooltip, Typography } from '@mui/material';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { marketAreaApiKeyStore } from '../../../api/masters/market-area-api-key-store';
import { skillSetApiKeyStore } from '../../../api/masters/skill-set-api-key-store';
import { equipmentApiKeyStore } from '../../../api/masters/equipment-api-key-store';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { inspectorsColumns } from './inspectors-grid-columns';
import { UserPermissionName } from '../../../models/user-permission-name';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';
import { GridColumnConfig } from '../../../models/gridCoumnConfig';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { GridCellProps } from '@progress/kendo-react-grid';


interface CellProps {
  inspectorData: any;
}

interface CustomGridCellProps {
  field: string;
}

export const SelectInspector = () => {
  const { setValue, getValues } = useFormContext();
  const [equipmentIDs, setEquipmentIDs] = useState<any>();
  const [skillsetIDs, setSkillsetIDs] = useState<any>();
  const [marketAreaIDs, setMarketAreaIDs] = useState<any>();
  const [userIDs, setUserIDs] = useState<any>();
  const [completeData, setCompleteData] = useState<any[]>();
  const [isOpenSnackbar, setIsOpenSnackbar] = useState(false);
  const [message, setMessage] = useState<string>();
  const [onReportCheckbox, setReportCheckbox] = useState<boolean>(false);
  const [selectedInspector, setSelectedInspector] = useState<any>();
  const { isLoading, data, mutateAsync } = useSaveData<any>(usersApiKeyStore.updateInspectors());

  const { isLoading: isSecondaryInsLoading, isFetching: isSecondaryInsFetching, data: secondaryData } = useLoadData<ApiResponse<any>>(
    usersApiKeyStore.getUserSecondayInspectors(),
    !!onReportCheckbox
  );

  const updatedInspectorsColumns = useMemo<GridColumnConfig[]>(() => {
    if (!onReportCheckbox) {
      return inspectorsColumns;
    }

    return [
      ...inspectorsColumns,
      {
        title: 'Seconday Inspector',
        field: 'isSecondaryInspector',
        filter: 'text',
        searchable: false,
        isGroupable: false,
        isHideColumnMenu: true
      },
    ];
  }, [onReportCheckbox]);

  const setInspectorData = (inspectorData: any) => {
    const selectedID = getValues('inspectorID');
    const secondaryInsID = getValues('secondaryInspectorID') ?? null;
    const updatedData = inspectorData?.map((d: any) => ({
      ...d,
      selected: selectedID === d.userID,
      secondaryInspector: secondaryInsID
    }));
    setCompleteData(updatedData);
    setSelectedInspector(selectedID);
    setReportCheckbox(getValues('remoteInspection') ?? false);
  }

  useEffect(() => {
    if (data?.data) {
      setInspectorData(data?.data);
    }
  }, [data]);

  const handleSelectedInspector = (values: any[]) => {
    if (values?.length == 0) { setValue('inspectorID', 0); }
    else if (values && values.length > 0) {
      if (values[0]?.hasDuplicateEquipments) {
        setIsOpenSnackbar(true);
        setMessage(values[0]?.equipmentDuplicateWarningMsg)
        setInspectorData(data?.data);
        setValue('inspectorID', 0);
        setSelectedInspector(0);
      }
      else {
        setValue('inspectorID', values[0].userID);
        setSelectedInspector(values[0].userID);
      }
    }
    setValue('secondaryInspectorID', null);
    setCompleteData(prevData =>
      prevData?.map(inspector => ({
        ...inspector,
        selected: inspector?.userID === values[0]?.userID,
        secondaryInspector: null,
      }))
    );
  };

  useEffect(() => {
    mutateAsync({
      data: {
        workOrderID: getValues('workOrderID'),
      },
    });
  }, []);

  const handleFilterClick = () => {
    mutateAsync({
      data: {
        skillsets: skillsetIDs?.map((p: any) => p.skillsetID),
        equipments: equipmentIDs?.map((p: any) => p.equipmentID),
        marketAreas: marketAreaIDs?.map((p: any) => p.marketAreaID),
        inspectors: userIDs?.map((p: any) => p.userID),
        workOrderID: getValues('workOrderID'),
      },
    });
  };

  const handleOnReportInspection = (event: any) => {
    const isReportChecked = event?.target?.checked;
    setReportCheckbox(isReportChecked);
    if (!isReportChecked) {
      setValue('secondaryInspectorID', null);
      setCompleteData(prevData =>
        prevData?.map(inspector => ({
          ...inspector,
          secondaryInspector: null,
        }))
      );
    }
  }

  const handleSecondaryInspectorChange = (event: any, userID: any) => {
    const selectedSecondaryInspector = event.target.value;
    setValue('secondaryInspectorID', selectedSecondaryInspector);
    setCompleteData(prevData =>
      prevData?.map(inspector =>
        inspector.userID === userID
          ? { ...inspector, secondaryInspector: selectedSecondaryInspector }
          : inspector
      )
    );
  }

  const SecondaryInspectorCell = ({ inspectorData }: CellProps) => {
    const inspectorValue = inspectorData?.secondaryInspector;

    return (
      <td>
        {inspectorData?.selected && (
          <>
            <LoadingIndicator isLoading={isSecondaryInsLoading || isSecondaryInsFetching} />
            <FormControl fullWidth>
              <InputLabel size="small" id="demo-simple-select-label">Select Secondary Inspector</InputLabel>
              <Select
                size="small"
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={inspectorValue}
                label="Select Secondary Inspector"
                onChange={(event) => handleSecondaryInspectorChange(event, inspectorData?.userID)}
              >
                {secondaryData?.data
                  ?.filter((item: any) => item.userID !== selectedInspector)
                  .map((item: any) => (
                    <MenuItem key={item.userID} value={item.userID}>
                      {item.userName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </>
        )}
      </td>
    );
  };

  const SkillSetCell = ({ inspectorData }: CellProps) => {
    const equipmentValue = inspectorData?.userSkillsets;
    const equipmentPercentage = inspectorData?.skillSetMatchPercentage || 0;

    // Determine the color based on the progress value
    const getColor = (value: number) => {
      if (value >= 100) return 'green';
      if (value >= 50 && value < 80) return 'yellow';
      if (value >= 30 && value < 50) return 'orange';
      return 'red';
    };

    const progressColor = getColor(equipmentPercentage);

    return (
      <td>
        <>
          <p style={{ paddingBottom: 3 }}>{equipmentValue || ''}</p>
          {/* <Tooltip title={<Typography variant="body1">{equipmentValue || 'N/A'}</Typography>}> */}
            <Box display="flex" alignItems="center" flexDirection="column" gap={1}>
              {/* Progress Bar */}
              <Box
                sx={{
                  width: '100%',
                  height: 10,
                  backgroundColor: '#e0e0e0',
                  borderRadius: 5,
                  overflow: 'hidden',
                  position: 'relative',
                  // cursor: 'pointer',
                }}
              // display="flex"
              // flexDirection="row"
              >
                <LinearProgress
                  variant="determinate"
                  value={equipmentPercentage}
                  sx={{
                    height: '100%',
                    backgroundColor: 'transparent',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: progressColor,
                    },
                  }}
                />
              </Box>
              {/* Percentage Text */}
              <Typography
                variant="body2"
                sx={{
                  // color: progressColor,
                  fontWeight: 'bold',
                }}
              >
                {equipmentPercentage?.toFixed(2)}%
              </Typography>
            </Box>
          {/* </Tooltip> */}
        </>
      </td>
    );
  };

  const EquipementsCell = ({ inspectorData }: CellProps) => {
    const skillSetValue = inspectorData?.userEquipments;
    const skillSetPercentage = inspectorData?.equipmentMatchPercentage || 0;

    // Determine the color based on the progress value
    const getColor = (value: number) => {
      if (value >= 100) return 'green';
      if (value >= 50 && value < 80) return 'yellow';
      if (value >= 30 && value < 50) return 'orange';
      return 'red';
    };

    const progressColor = getColor(skillSetPercentage);

    return (
      <td>
        <>
          <p style={{ paddingBottom: 3 }}>{skillSetValue || ''}</p>
          {/* <Tooltip title={<Typography variant="body1">{skillSetValue || 'N/A'}</Typography>}> */}
            <Box display="flex" alignItems="center" flexDirection="column" gap={1}>
              {/* Progress Bar */}
              <Box
                sx={{
                  width: '100%',
                  height: 10,
                  backgroundColor: '#e0e0e0',
                  borderRadius: 5,
                  overflow: 'hidden',
                  position: 'relative',
                  // cursor: 'pointer',
                }}
              // display="flex"
              // flexDirection="row"
              >
                <LinearProgress
                  variant="determinate"
                  value={skillSetPercentage}
                  sx={{
                    height: '100%',
                    backgroundColor: 'transparent',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: progressColor,
                    },
                  }}
                />
              </Box>
              {/* Percentage Text */}
              <Typography
                variant="body2"
                sx={{
                  // color: progressColor,
                  fontWeight: 'bold',
                }}
              >
                {skillSetPercentage?.toFixed(2)}%
              </Typography>
            </Box>
          {/* </Tooltip> */}
        </>
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps) => {
    if (field === 'isSecondaryInspector') {
      return (dataItem: any) => {
        return <SecondaryInspectorCell inspectorData={dataItem.dataItem} />;
      };
    }
    // if (field === 'userSkillsets') {
    //   return (dataItem: any) => {
    //     return <SkillSetCell inspectorData={dataItem.dataItem} />;
    //   };
    // }
    // if (field === 'userEquipments') {
    //   return (dataItem: any) => {
    //     return <EquipementsCell inspectorData={dataItem.dataItem} />;
    //   };
    // }
  };

  const conditionalCellRender = (
    defaultRendering: React.ReactElement<HTMLTableCellElement> | null,
    props: GridCellProps
  ) => {
    if (
      props.field === 'selected' &&
      (props?.dataItem?.skillSetMatchPercentage ===0
        //|| props?.dataItem?.matchPercentageEquipment !== 100)
      )
    ) {
      return (
        <Tooltip title={<Typography variant="body1">
          The Primary Inspector does not have any matching skill set(s) for the selected inspection.</Typography>}>
          <td>
            <Checkbox
              checked={props.dataItem?.selected === true}
              disabled={
                props?.dataItem?.skillSetMatchPercentage === 0
              }
            />
          </td>
        </Tooltip>
      );
    } else {
      return defaultRendering;
    }
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid item lg={3} sm={6} xl={3} xs={6}>
          <FormAutoCompleteMultiSelectDynamic
            name="userIDs"
            label="Inspectors"
            getQueryKeyConfig={() => usersApiKeyStore.getUsersByRole('ins')}
            optionsLabelKey="fullName"
            optionsValueKey="userID"
            onChange={(values: any) => setUserIDs(values)}
          />
        </Grid>
        <Grid item lg={3} sm={6} xl={3} xs={6}>
          <FormAutoCompleteMultiSelectDynamic
            name="marketAreaIDs"
            label="Market Areas"
            getQueryKeyConfig={() => marketAreaApiKeyStore.getMarketAreas()}
            optionsLabelKey="marketAreaName"
            optionsValueKey="marketAreaID"
            onChange={(values: any) => setMarketAreaIDs(values)}
          />
        </Grid>
        <Grid item lg={3} sm={6} xl={3} xs={6}>
          <FormAutoCompleteMultiSelectDynamic
            name="skillsetIDs"
            label="Skills"
            getQueryKeyConfig={() => skillSetApiKeyStore.getSkillSets()}
            optionsLabelKey="skillsetName"
            optionsValueKey="skillsetID"
            onChange={(values: any) => setSkillsetIDs(values)}
          />
        </Grid>
        <Grid item xl={2} lg={3} sm={6} xs={6}>
          <FormAutoCompleteMultiSelectDynamic
            name="equipmentIDs"
            label="Equipments"
            getQueryKeyConfig={() => equipmentApiKeyStore.getEquipments()}
            optionsLabelKey="equipmentName"
            optionsValueKey="equipmentID"
            onChange={(values: any) => setEquipmentIDs(values)}
          />
        </Grid>

        <Grid item xl={1} lg={1} sm={3} xs={6}>
          <Button type="button" onClick={handleFilterClick} title="Filter" className="btn btn-md btn-primary">
            Search
          </Button>
        </Grid>
        <Grid item xl={12} lg={12} sm={12} xs={12}>
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: "flex-end",
              alignItems: "flex-start",
            }}>
            <FormCheckbox label="Remote Inspection" name="remoteInspection" onChange={handleOnReportInspection} />
          </Grid>
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <SiTrackGrid
            fileTitle='workorder_inspector'
            columns={updatedInspectorsColumns}
            dataItemKey="userID"
            selectedField="selected"
            selectionMode="single"
            customCell={(field) =>
              CustomGridCell({
                field,
              })
            }
            // conditionalCellRender={conditionalCellRender}
            onSelectedItems={handleSelectedInspector}
            dataItems={completeData}
            actionPermissions={{
              export: UserPermissionName.DOWNLOAD_GRID,
            }}
            isBusy={isLoading}
          />
        </Grid>
      </Grid >
      <Snackbar
        open={isOpenSnackbar}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setIsOpenSnackbar(false)}
      >
        <Alert severity="error" variant="filled" sx={{ width: '100%' }} onClose={() => setIsOpenSnackbar(false)}>
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};
